import { useCallback, useEffect, useReducer, useRef } from 'react';
// Relative, not the `@shared-client` alias every other consumer uses: this module
// is the one with a unit test, and `bun test` resolves neither vite aliases nor a
// tsconfig `paths` (studio deliberately has no tsconfig). The alias is fine in
// app-only files, which vite is the only thing that ever loads.
import { useVisibleSet } from '../../../../shared/client/useVisibleSet.ts';

/**
 * The dashboard's cover-thumbnail warming queue.
 *
 * `studio/CLAUDE.md` ("How slides are previewed") says a LISTING must never
 * generate thumbnails, because generating spawns the deck's Vite child and the
 * pool is only 4 wide and shared with the `__workbench` child. The dashboard is
 * the one place that bends that rule, deliberately and with every safeguard
 * intact — see the "Deck cards carry a cover" note in that file for why the pool
 * argument is weaker here (nothing is being edited, and this page is not itself
 * served by a pooled child).
 *
 * The safeguards, all of which this module owns:
 *
 * - **Demand-driven, and the demand SHRINKS.** The queue is the set of decks
 *   currently on screen, replaced as the viewport moves — not a growing list of
 *   everything that has ever been scrolled past. That distinction is load
 *   bearing: while this was a one-way "this card appeared" signal, flying down a
 *   34-deck grid queued all 34, each costing a Vite child and a Chromium, served
 *   strictly first-come. Scrolling back up then left the cards in front of the
 *   user waiting behind decks they had merely passed, which reads as the page
 *   having hung — and did (Alex, 2026-08-03). A deck that leaves the viewport
 *   leaves the queue, and the running job is retired with it.
 * - **Serial.** One deck generating at a time, so the pool is never raced.
 * - **Never blocked by a pass it didn't start.** Serial is what makes generating
 *   from a listing safe, and it is also what makes head-of-line blocking
 *   possible. A deck whose WHOLE-DECK pass is already running answers `busy`:
 *   our cover was never started and that pass can take minutes, so the deck goes
 *   to the back of the queue (`defer`) instead of holding the runner while every
 *   other card waits. Observed before this existed: an 18-slide deck pinned the
 *   queue and no other cover appeared.
 * - **Off entirely at the small card size**, where there is no image to show.
 * - **Terminal.** Every poll can end: the server's own backoff (`coolingUntil`),
 *   a `pending: false` answer (nothing is coming — an empty deck, or generation
 *   refused), and a wall-clock cap. Note the whole-deck pollers additionally
 *   watch a pending COUNT for stalling; a cover is a single image with no count
 *   to watch, so that condition collapses into the wall clock here.
 * - **Remembers giving up**, for the browser session, so a deck that cannot
 *   render doesn't cost four minutes of polling on every visit to the page.
 */

// Matches the whole-deck pollers (DeckOverview, Library, the gallery's warm
// runner). Fast enough to feel immediate, slow enough not to hammer the server.
const POLL_MS = 800;

// Backstop for a capture that never settles. Generous on purpose: a COLD deck's
// first capture waits on its Vite child compiling the whole module graph, which
// the server allows 45s for, and the server's own pass timeout is 180s. Below
// that and we would write off decks that were about to succeed.
const GIVE_UP_MS = 240_000;

// How long to leave a deck alone when the server says a WHOLE-DECK pass owns it
// (`busy`). There is nothing to wait for — that pass renders slide 1 anyway — so
// the deck goes to the back of the queue and everything else proceeds.
export const DEFER_MS = 5_000;

// How many times one deck may step aside before we stop asking. At DEFER_MS
// apart this is about a minute of someone else's pass, which is longer than a
// typical whole-deck render; past that the cover will be cached by the time the
// user next loads the page anyway.
export const MAX_DEFERS = 12;

/** One deck waiting for its cover. `notBefore` is how a deferred deck waits its
 *  turn without occupying the single active slot. */
export type CoverJob = { slug: string; notBefore: number; defers: number };

export type CoverState = {
  queue: CoverJob[];
  /** The one deck being rendered — the runner is serial (a Vite child plus a
   *  Chromium per deck). */
  active: string | null;
  activeJob: CoverJob | null;
  done: string[];
  /** Decks given up on, persisted to sessionStorage so a reload does not
   *  silently retry them. `retry` is the way back. */
  stalled: string[];
};

export type CoverAction =
  | { type: 'visible'; slugs: string[]; now: number }
  | { type: 'start'; now: number }
  | { type: 'done'; slug: string }
  | { type: 'defer'; slug: string; now: number; kind?: 'deck' | 'capacity' }
  | { type: 'fail'; slug: string }
  | { type: 'retry'; slug: string }
  | { type: 'clear' };

export const INITIAL_QUEUE: CoverState = { queue: [], active: null, activeJob: null, done: [], stalled: [] };

// Decks we have given up on, remembered for the browser session. Session-scoped
// rather than localStorage: fixing the deck and reloading should be enough to
// get another go.
const STALLED_KEY = 'dashboard.stalledCovers';

function readStalled() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(STALLED_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((s) => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

function writeStalled(list: string[]): void {
  try {
    sessionStorage.setItem(STALLED_KEY, JSON.stringify(list));
  } catch {
    /* private mode / quota — we just retry this deck next visit */
  }
}

// Queue entries are { slug, notBefore, defers } — `notBefore` is how a deferred
// deck waits its turn without occupying the single active slot. Derived from the
// exported shape so the runtime and its test can't drift apart.
const INIT: CoverState = { ...INITIAL_QUEUE, stalled: readStalled() };

// Takes the queue, not the whole state: `warmingNow` is a useCallback whose
// deps are the fields it actually reads, and passing `state` there would make
// exhaustive-deps demand the entire object.
const queued = (queue: CoverJob[], slug: string): boolean => queue.some((j) => j.slug === slug);

/** Exported for its unit test — the queue's decisions (skip a deferred deck,
 *  stop asking after MAX_DEFERS, never re-queue a settled one) are the part
 *  worth pinning down, and they are pure. */
export function reducer(state: CoverState, action: CoverAction): CoverState {
  switch (action.type) {
    // The decks currently ON SCREEN. Replaces the queue rather than adding to it,
    // and that is the whole point: this used to be a one-way `want`, so flying
    // down a 34-deck grid queued all 34 in the order they crossed the viewport,
    // and each costs a Vite child plus a Chromium. Scroll back up and the cards
    // in front of you were stuck behind decks you had merely passed — minutes of
    // grey tiles that reads, fairly, as the page having hung.
    case 'visible': {
      const { slugs } = action;
      const queue: CoverJob[] = [];
      for (const slug of slugs) {
        if (state.active === slug) continue; // already running
        if (state.done.includes(slug) || state.stalled.includes(slug)) continue;
        // Keep a deck's defer timer if it was already waiting, or a busy deck
        // would have its backoff reset by every scroll tick.
        const i = state.queue.findIndex((j) => j.slug === slug);
        const existing = i === -1 ? undefined : state.queue[i];
        queue.push(existing ?? { slug, notBefore: 0, defers: 0 });
      }
      // Retire the running job when its card has scrolled away. The server pass
      // cannot be called back and finishes regardless — its image lands in the
      // cache and the next visit picks it up — but the client must stop waiting,
      // because the runner is serial and everything on screen is behind it.
      const keepActive = state.active && slugs.includes(state.active);
      return keepActive ? { ...state, queue } : { ...state, queue, active: null, activeJob: null };
    }
    case 'start': {
      if (state.active || state.queue.length === 0) return state;
      // The first deck that is actually due. A deferred one is skipped rather
      // than blocking the decks behind it — that head-of-line block is the whole
      // reason `notBefore` exists.
      const i = state.queue.findIndex((j) => j.notBefore <= action.now);
      if (i === -1) return state;
      const queue = state.queue.slice();
      const [next] = queue.splice(i, 1);
      // splice always yields one element for an index it just found; the type
      // does not know that, and a missing job means nothing is due.
      if (!next) return state;
      return { ...state, active: next.slug, queue, activeJob: next };
    }
    case 'done':
      return { ...state, active: null, activeJob: null, done: [...state.done, action.slug] };
    // A whole-deck pass owns this slug. Not a failure and not our wait: go to the
    // back of the queue, let every other card have the runner, and try again once
    // that pass has had a chance to finish (it renders slide 1 for us anyway).
    case 'defer': {
      const job = state.activeJob;
      // A `'deck'` busy means OUR own whole-deck pass is rendering slide 1 for
      // us and will land it — that wait is productive and must not consume the
      // give-up budget. Only a `'capacity'` busy (the global pass slot held by
      // a DIFFERENT deck, so nothing we want is being made) is the unproductive
      // wait worth capping. Backward compatible: an absent kind counts, as
      // before.
      const counted = action.kind !== 'deck';
      const defers = counted ? (job?.defers || 0) + 1 : job?.defers || 0;
      if (defers > MAX_DEFERS) {
        const stalled = [...state.stalled, action.slug];
        writeStalled(stalled);
        return { ...state, active: null, activeJob: null, stalled };
      }
      return {
        ...state,
        active: null,
        activeJob: null,
        queue: [...state.queue, { slug: action.slug, notBefore: action.now + DEFER_MS, defers }],
      };
    }
    case 'fail': {
      const stalled = [...state.stalled, action.slug];
      writeStalled(stalled);
      return { ...state, active: null, activeJob: null, stalled };
    }
    // The user pressed the refresh control on a stalled card. Lift every reason
    // this deck stopped being asked for — the session-scoped give-up AND any
    // 'done' record — and put it back in the queue at the front of the wait.
    //
    // A stalled cover used to be terminal with no way out: a permanently grey
    // box on the dashboard, against the studio's own rule that a warning must
    // always offer one. Session storage is written back here too, or a reload
    // would restore the give-up this is meant to lift.
    case 'retry': {
      const stalled = state.stalled.filter((s) => s !== action.slug);
      writeStalled(stalled);
      return {
        ...state,
        stalled,
        done: state.done.filter((s) => s !== action.slug),
        queue: queued(state.queue, action.slug)
          ? state.queue.map((j) => (j.slug === action.slug ? { ...j, notBefore: 0, defers: 0 } : j))
          : [{ slug: action.slug, notBefore: 0, defers: 0 }, ...state.queue],
      };
    }
    // The size control moved to Small, or the page unmounted. A pass already
    // RUNNING on the server plays out — it cannot be cancelled and its Vite
    // child is already spawned — but nothing new starts.
    case 'clear':
      return { ...state, queue: [] };
    default:
      return state;
  }
}

/**
 * @param {object} opts
 * @param {boolean} opts.enabled  False at the small card size: nothing is queued
 *   and nothing generates.
 * @param {(slug: string, url: string) => void} opts.onCover  Called once with the
 *   cover URL when a deck's capture lands.
 * @returns {{ warmingNow: (slug: string) => boolean, coverRef: (slug: string) => (el: Element|null) => void }}
 *   `warmingNow` drives whether a placeholder shimmers — animating one for a
 *   deck nothing is working on would promise something that is not coming.
 *   `coverRef` is a stable-per-slug ref callback: attach it to a card that has
 *   no cover and that deck is warmed while the card is on screen — and dropped
 *   again when it is not.
 */
export type CoverWarm = {
  /** This deck's cover is queued or rendering — what a shimmer should key on. */
  warmingNow: (slug: string) => boolean;
  /** Not coming unless the user asks again. */
  coverStalled: (slug: string) => boolean;
  retryCover: (slug: string) => void;
  /** Attach to each card: scrolling to it IS the request. */
  coverRef: (slug: string) => (el: Element | null) => void;
};

export function useCoverWarm({
  enabled,
  onCover,
}: {
  enabled: boolean;
  onCover: (slug: string, url: string) => void;
}): CoverWarm {
  const [state, dispatch] = useReducer(reducer, INIT);
  // Kept in a ref so the runner effect below depends only on `active` — reading
  // the callback through a ref stops a new `onCover` identity from restarting a
  // poll that is already in flight.
  const onCoverRef = useRef(onCover);
  onCoverRef.current = onCover;

  // The observer is the shared one (`shared/client/useVisibleSet.ts`), which
  // reports what is on screen as a SET — entries AND exits — rather than a
  // stream of "this appeared" events. That distinction is the whole fix for the
  // scroll-down-and-back hang: a one-way signal can only ever grow the queue.
  const { visible, observe } = useVisibleSet({ enabled });

  useEffect(() => {
    if (!enabled) {
      dispatch({ type: 'clear' });
      return;
    }
    dispatch({ type: 'visible', slugs: visible, now: Date.now() });
  }, [visible, enabled]);

  // Pull the next DUE deck off the queue whenever the runner is free. When every
  // waiting deck is deferred there is nothing to start now, so wake up again when
  // the earliest one comes due — otherwise a queue of deferred decks would sit
  // there until some other state change happened to re-run this.
  useEffect(() => {
    if (state.active || state.queue.length === 0) return undefined;
    const now = Date.now();
    const soonest = Math.min(...state.queue.map((j) => j.notBefore));
    if (soonest <= now) {
      dispatch({ type: 'start', now });
      return undefined;
    }
    const timer = setTimeout(() => dispatch({ type: 'start', now: Date.now() }), soonest - now);
    return () => clearTimeout(timer);
  }, [state.active, state.queue]);

  // The runner: generate, then poll until it lands or a terminal condition hits.
  useEffect(() => {
    const slug = state.active;
    if (!slug) return undefined;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    /** The subset of `GET /api/decks/:slug/cover` this runner reads. */
    const settle = (
      status: {
        url?: string;
        busy?: boolean;
        busyKind?: 'deck' | 'capacity';
        /** Deck-wide backoff: the server has stopped trying for now. */
        coolingUntil?: number | null;
        pending?: string[];
      } | null,
    ) => {
      if (stopped) return;
      if (status?.url) {
        onCoverRef.current?.(slug, status.url);
        dispatch({ type: 'done', slug });
        return;
      }
      // A whole-deck pass owns this slug, so our cover was never started. Waiting
      // here would hold the runner for however long THAT pass takes — an 18-slide
      // deck is a minute or more — and every other card's cover would queue
      // behind a deck the user may not even be looking at. Step aside instead.
      // `busyKind` tells an own-deck pass (productive — it renders slide 1 for
      // us) from another deck holding the global slot (nothing we want is being
      // made); only the latter consumes the give-up budget.
      if (status?.busy) {
        dispatch({ type: 'defer', slug, now: Date.now(), kind: status.busyKind });
        return;
      }
      // The server has backed off, or is telling us nothing is coming (an empty
      // deck, or a pass it refused). Either way this poll is over.
      if (status && ((status.coolingUntil ?? 0) > Date.now() || !status.pending)) {
        dispatch({ type: 'fail', slug });
        return;
      }
      if (Date.now() - startedAt > GIVE_UP_MS) {
        dispatch({ type: 'fail', slug });
        return;
      }
      timer = setTimeout(poll, POLL_MS);
    };

    const request = (generate: boolean) =>
      fetch(`/api/decks/${slug}/cover${generate ? '' : '?generate=0'}`, { credentials: 'same-origin' })
        .then((res) => (res.ok ? res.json() : null))
        .then(settle)
        // Never strand the queue: whatever the transport did, this deck is done
        // being waited on and the next one gets its turn.
        .catch(() => {
          if (!stopped) dispatch({ type: 'fail', slug });
        });

    const poll = () => request(false);
    request(true);

    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [state.active]);

  const warmingNow = useCallback(
    (slug: string) => state.active === slug || queued(state.queue, slug),
    [state.active, state.queue],
  );
  /** This deck's cover is not coming unless the user asks again. */
  const coverStalled = useCallback((slug: string) => state.stalled.includes(slug), [state.stalled]);
  const retryCover = useCallback((slug: string) => dispatch({ type: 'retry', slug }), []);

  return { warmingNow, coverStalled, retryCover, coverRef: observe };
}
